/**
 * DATABANK FORM - WITH REACT QUERY
 * ICR x Date x Inverter grid with auto-totals
 * @version 4.0.0 - Smart caching, queue management, circuit breaker
 * FIXED: saveBatch now working
 */

import React, { useState, useRef, useEffect, useMemo, useLayoutEffect, useCallback } from 'react';
import { useVirtualizer, VirtualItem } from '@tanstack/react-virtual';
import { Database, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { FormActions, StatusBanner, SummaryModal, CorrectMistakeModal } from '../../../shared';
import { DateRangeFilter } from '../../../shared';
import { ALL_ICRS, getInvertersForICR, calculateICRSum } from '../forms_data/databankColumns';
import { useFormData } from '../../../../../../core/hooks/useFormData';
import { DatabankDataRow, databankService, loadAllPaginatedData } from '../services/axonApiService';
import { useAuth } from '../../../../../../context/AuthContext';
import backendApi from '../../../../../../services/backendApi';
import { Edit } from 'lucide-react';
import { ColumnDefinition } from '../../../universal_table/types/column.types';
import { resolveUserFilterId, shouldLoadAllRecords } from '../utils/userDataAccess';
import { buildUserScopedStorageKey } from '../utils/storageKeys';
import { buildGenerationReportFooterText } from '../utils/generationReportFooter';

// Normalize ICR and Inverter values to match expected format
const normalizeICR = (icr: string): string => {
  if (!icr) return '';
  // Handle formats like "ICR-01", "ICR 01", "ICR01", "ICR1" -> "ICR1"
  const normalized = icr.trim().toUpperCase()
    .replace(/^ICR[\s\-_]*/i, 'ICR')
    .replace(/[\s\-_]/g, '');
  // Ensure format is ICR## (e.g., ICR1, ICR01, ICR23)
  const match = normalized.match(/^ICR(\d+)$/i);
  if (match) {
    const num = parseInt(match[1], 10);
    return `ICR${num}`; // Remove leading zeros: ICR01 -> ICR1
  }
  return normalized; // Return as-is if format doesn't match
};

const normalizeInverter = (inverter: string): string => {
  if (!inverter) return '';
  // Handle formats like "Inverter_01", "Inverter 01", "Inverter01", "Inverter1", "INV1", "INV_01" -> "INV1"
  // The mapping file uses "INV1", "INV2", etc. (not "Inverter1")
  let normalized = inverter.trim().toUpperCase();

  // Remove "INVERTER" prefix and convert to "INV" format
  normalized = normalized.replace(/^INVERTER[\s\-_]*/i, 'INV');
  normalized = normalized.replace(/[\s\-_]/g, '');

  // Ensure format is INV## (e.g., INV1, INV01, INV23)
  const match = normalized.match(/^INV(\d+)$/i);
  if (match) {
    const num = parseInt(match[1], 10);
    return `INV${num}`; // Remove leading zeros: INV01 -> INV1
  }

  // If already in INV format, return as-is
  if (normalized.startsWith('INV')) {
    return normalized;
  }

  // Fallback: try to extract number and convert to INV format
  const numMatch = normalized.match(/(\d+)/);
  if (numMatch) {
    const num = parseInt(numMatch[1], 10);
    return `INV${num}`;
  }

  return normalized;
};

interface DatabankStyleVars {
  h: number; hh: number; w: number; icrW: number; invW: number; fs: number; hfs: number;
}

interface DatabankIcrGroupProps {
  icr: string;
  icrIdx: number;
  groupData: Record<string, Record<string, string>> | undefined;
  filteredDates: string[];
  virtualColumns: VirtualItem[];
  columnPaddingLeft: number;
  columnPaddingRight: number;
  styleVars: DatabankStyleVars;
  focusedCell: { rowIndex: number; colIndex: number; inverterIndex: number };
  inputRefs: React.MutableRefObject<any>;
  onCellChange: (icr: string, date: string, inv: string, value: string) => void;
  onCellKeyDown: (e: React.KeyboardEvent, icrIdx: number, colIdx: number, invIdx: number) => void;
  onCellFocus: (icrIdx: number, colIdx: number, invIdx: number) => void;
}

/**
 * One ICR's inverter rows + its TOTAL row. Memoized so editing a cell in one ICR
 * doesn't re-render the other ~33 ICR groups (updateCell only replaces the edited
 * ICR's slice of `data`, so `groupData` stays reference-equal for unrelated groups).
 */
const DatabankIcrGroupComponent: React.FC<DatabankIcrGroupProps> = ({
  icr,
  icrIdx,
  groupData,
  filteredDates,
  virtualColumns,
  columnPaddingLeft,
  columnPaddingRight,
  styleVars,
  focusedCell,
  inputRefs,
  onCellChange,
  onCellKeyDown,
  onCellFocus
}) => {
  const invs = getInvertersForICR(icr);

  const formatValue = (val: string): string => {
    if (!val || val.toString().trim() === '') return '';
    const num = parseFloat(val.toString().trim());
    if (isNaN(num) || !isFinite(num)) return val.toString();
    return num.toFixed(1);
  };

  return (
    <>
      {invs.map((inv, invIdx) => (
        <tr key={`${icr}-${inv}`} style={{ height: `${styleVars.h}px` }}>
          {invIdx === 0 && (
            <td className="sticky-icr-col" style={{ width: `${styleVars.icrW}px`, background: 'var(--color-white)', color: '#000000', fontWeight: 700, textAlign: 'center', verticalAlign: 'middle' }} rowSpan={invs.length + 1}>
              <span style={{ color: '#000000', fontWeight: 700, display: 'block', width: '100%', textAlign: 'center' }}>{icr}</span>
            </td>
          )}
          <td className="sticky-inv-col" style={{ width: `${styleVars.invW}px`, background: 'var(--color-gray-50)', textAlign: 'center', fontWeight: 700, color: '#000000', verticalAlign: 'middle' }}>
            <span style={{ color: '#000000', fontWeight: 700, display: 'block', width: '100%', textAlign: 'center' }}>{inv}</span>
          </td>
          {columnPaddingLeft > 0 && (
            <td aria-hidden="true" style={{ width: `${columnPaddingLeft}px`, padding: 0, border: 'none' }} />
          )}
          {virtualColumns.map((vCol) => {
            const colIdx = vCol.index;
            const date = filteredDates[colIdx];
            if (date === undefined) return null;

            const rawValue = groupData?.[date]?.[inv] || '';
            const selected = focusedCell.rowIndex === icrIdx && focusedCell.colIndex === colIdx && focusedCell.inverterIndex === invIdx;
            const displayValue = selected ? rawValue : formatValue(rawValue);

            return (
              <td key={colIdx} style={{ width: `${styleVars.w}px` }}>
                <input
                  ref={el => {
                    inputRefs.current[`${icrIdx}:${colIdx}:${invIdx}`] = el;
                    if (el) {
                      el.style.setProperty('font-weight', '400', 'important');
                    }
                  }}
                  type="number"
                  step="0.1"
                  style={{
                    fontSize: `${styleVars.fs}px`,
                    height: `${styleVars.h}px`,
                    border: selected ? '2px solid #0084CF' : '1px solid #e5e7eb',
                    background: rawValue ? '#ffffff' : '#f9fafb',
                    width: '100%',
                    textAlign: 'center',
                    color: '#111827',
                    fontWeight: 400,
                    borderRadius: '4px',
                    transition: 'all 0.15s ease',
                    boxShadow: selected ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
                    padding: '4px 2px 4px 14px'
                  }}
                  value={displayValue}
                  onChange={(e) => onCellChange(icr, date, inv, e.target.value)}
                  onBlur={(e) => {
                    // Format to 1 decimal place on blur
                    const val = e.target.value;
                    if (val && val.toString().trim() !== '') {
                      const num = parseFloat(val.toString().trim());
                      if (!isNaN(num) && isFinite(num)) {
                        const formatted = num.toFixed(1);
                        onCellChange(icr, date, inv, formatted);
                      }
                    }
                  }}
                  onKeyDown={(e) => onCellKeyDown(e, icrIdx, colIdx, invIdx)}
                  onFocus={() => onCellFocus(icrIdx, colIdx, invIdx)}
                />
              </td>
            );
          })}
          {columnPaddingRight > 0 && (
            <td aria-hidden="true" style={{ width: `${columnPaddingRight}px`, padding: 0, border: 'none' }} />
          )}
        </tr>
      ))}
      <tr className="total-row" style={{ height: `${styleVars.h}px`, background: 'var(--color-primary-blue-light)' }}>
        <td className="sticky-inv-col" style={{ width: `${styleVars.invW}px`, fontWeight: 700, color: '#000000', textAlign: 'center', background: 'var(--color-primary-blue-light) !important', borderRight: '1px solid #e5e7eb' }}>
          <span style={{ color: '#000000', fontWeight: 700, display: 'block', width: '100%', fontSize: `${styleVars.fs + 2}px` }}>TOTAL</span>
        </td>
        {columnPaddingLeft > 0 && (
          <td aria-hidden="true" style={{ width: `${columnPaddingLeft}px`, padding: 0, border: 'none' }} />
        )}
        {virtualColumns.map((vCol) => {
          const date = filteredDates[vCol.index];
          if (date === undefined) return null;
          return (
            <td key={date} className="total-value-cell" style={{ width: `${styleVars.w}px`, fontWeight: 700, color: '#000000', textAlign: 'center', background: 'var(--color-primary-blue-light)' }}>
              <span style={{ color: '#000000', fontWeight: 700, display: 'block', width: '100%', fontSize: `${styleVars.fs + 2}px` }}>
                {invs.reduce((sum, inv) => sum + (parseFloat(groupData?.[date]?.[inv] || '') || 0), 0).toFixed(1)}
              </span>
            </td>
          );
        })}
        {columnPaddingRight > 0 && (
          <td aria-hidden="true" style={{ width: `${columnPaddingRight}px`, padding: 0, border: 'none' }} />
        )}
      </tr>
    </>
  );
};

const databankIcrGroupPropsEqual = (prev: DatabankIcrGroupProps, next: DatabankIcrGroupProps): boolean => {
  if (prev.icr !== next.icr) return false;
  if (prev.icrIdx !== next.icrIdx) return false;
  if (prev.groupData !== next.groupData) return false;
  if (prev.filteredDates !== next.filteredDates) return false;
  if (prev.virtualColumns !== next.virtualColumns) return false;
  if (prev.columnPaddingLeft !== next.columnPaddingLeft) return false;
  if (prev.columnPaddingRight !== next.columnPaddingRight) return false;
  if (prev.styleVars !== next.styleVars) return false;

  // Only re-render on a focus change if it actually enters or leaves THIS group —
  // otherwise every ICR group would re-render whenever focus moves anywhere in the grid.
  const wasFocused = prev.focusedCell.rowIndex === prev.icrIdx;
  const isFocusedNow = next.focusedCell.rowIndex === next.icrIdx;
  if (wasFocused !== isFocusedNow) return false;
  if (isFocusedNow && (
    prev.focusedCell.colIndex !== next.focusedCell.colIndex ||
    prev.focusedCell.inverterIndex !== next.focusedCell.inverterIndex
  )) return false;

  return true;
};

// Callback props (onCellChange/onCellKeyDown/onCellFocus) are intentionally not
// compared — same rationale as universal_table's TableRow/TableCell: this file
// doesn't wrap every handler in useCallback, and a bailed-out group keeps whatever
// closure was wired up last time it actually rendered, which still calls through to
// stable underlying state setters.
const DatabankIcrGroup = React.memo(DatabankIcrGroupComponent, databankIcrGroupPropsEqual);

interface DatabankFormProps {
  site?: string;
}

export const DatabankForm: React.FC<DatabankFormProps> = ({ site }) => {
  const DBK_DEBUG = false; // âš¡ Set true for debug logging, false for production speed
  const [isCondensed, setIsCondensed] = useState(true);
  const [status, setStatus] = useState<any>({ type: null, message: '' });
  const [showSummary, setShowSummary] = useState(false);
  const [oldestLoadedDate, setOldestLoadedDate] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isInitialScrollDone, setIsInitialScrollDone] = useState(false);
  const scrollPositionRef = useRef<number | null>(null);
  // Store original loaded data to compare against changes
  const originalDataRef = useRef<any>({});

  // ✅ Track only user-edited/changed entries (key: "date|icr|inverter", value: DatabankDataRow)
  const changedEntriesRef = useRef<Map<string, DatabankDataRow>>(new Map());

  const [focusedCell, setFocusedCell] = useState({ rowIndex: 0, colIndex: 0, inverterIndex: 0 });
  const handleCellFocus = useCallback(
    (icrIdx: number, colIdx: number, invIdx: number) => setFocusedCell({ rowIndex: icrIdx, colIndex: colIdx, inverterIndex: invIdx }),
    []
  );
  const [showCorrectMistake, setShowCorrectMistake] = useState(false);
  const [correctMistakeData, setCorrectMistakeData] = useState<DatabankDataRow[]>([]);
  const [loadingCorrectMistake, setLoadingCorrectMistake] = useState(false);
  // Lazy loading state for edit popup
  const [correctMistakeLoadingMore, setCorrectMistakeLoadingMore] = useState(false);
  const [correctMistakeHasMore, setCorrectMistakeHasMore] = useState(true);
  const [correctMistakeLoadedRanges, setCorrectMistakeLoadedRanges] = useState<Array<{ start: string; end: string }>>([]);
  const correctMistakeOldestDateRef = useRef<string | null>(null);
  // Use ref instead of state to persist across re-renders and prevent reloading when switching sheets
  const isInitialLoadRef = useRef(true);
  const hasLoadedDataRef = useRef(false); // Track if we've already loaded data
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Keep for UI state if needed
  const [isExporting, setIsExporting] = useState(false);
  const [siteName, setSiteName] = useState<string>('axon');
  const [totalCountFromDB, setTotalCountFromDB] = useState<number | null>(null); // Total count from database
  // Date filtering state
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const { user } = useAuth();

  // Get site code from prop or default to 'axon'
  const siteCode = (site || 'axon').toLowerCase();

  const storageKey = useMemo(
    () => buildUserScopedStorageKey('unsaved_databank', siteCode, user),
    [siteCode, user?.username, user?.email, user?.fullName, user?.siteId]
  );

  const readPersistedGrid = (key: string): { data: any; dates: string[] } => {
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          data: parsed.data || {},
          dates: parsed.dates || []
        };
      }
    } catch (error) {
}
    return { data: {}, dates: [] };
  };

  const initialSnapshotRef = useRef<{ data: any; dates: string[] } | null>(null);
  if (!initialSnapshotRef.current) {
    const persisted = readPersistedGrid(storageKey);
    // Only use persisted data if it has 20 or fewer dates (to match initial load target)
    // If it has more dates, clear it and load fresh from backend. Note: the initial-load
    // effect below always re-fetches from the backend regardless and merges this
    // snapshot back in for any not-yet-saved edits — this is just to avoid flashing a
    // very large stale grid before that fresh fetch completes.
    if (persisted.dates && persisted.dates.length > 20) {
localStorage.removeItem(storageKey);
      initialSnapshotRef.current = { data: {}, dates: [] };
    } else {
      initialSnapshotRef.current = persisted;
    }
  }

  const [data, setData] = useState<any>(() => initialSnapshotRef.current?.data || {});
  const [dates, setDates] = useState<string[]>(() => initialSnapshotRef.current?.dates || []);

  const footerText = useMemo(
    () => buildGenerationReportFooterText({
      siteName,
      sheetName: 'Inverters',
      dateKeys: dates
    }),
    [siteName, dates]
  );

  // Store previous data/dates before applying date filter (for restoration)
  const previousDataRef = useRef<any>(null);
  const previousDatesRef = useRef<string[]>([]);
  const isFilteringRef = useRef(false);

  // Memoized filtered dates based on range
  const filteredDates = useMemo(() => {
    if (!startDate && !endDate) return dates;

    // Create Date objects for comparison (resetting time to midnight)
    const start = startDate ? new Date(startDate) : new Date('1900-01-01');
    start.setHours(0, 0, 0, 0);

    const end = endDate ? new Date(endDate) : new Date('2100-01-01');
    end.setHours(0, 0, 0, 0);

    return dates.filter(date => {
      const currentDate = new Date(date);
      currentDate.setHours(0, 0, 0, 0);
      return currentDate >= start && currentDate <= end;
    });
  }, [dates, startDate, endDate]);

  useEffect(() => {
    let isCancelled = false;

    const loadFilteredDatabankData = async () => {
      if (!startDate && !endDate) {
        if (isFilteringRef.current) {
          isFilteringRef.current = false;
          if (!isCancelled) {
            setData(previousDataRef.current || {});
            setDates(previousDatesRef.current || []);
          }
        }
        return;
      }

      if (!isFilteringRef.current) {
        previousDataRef.current = data;
        previousDatesRef.current = dates;
        isFilteringRef.current = true;
      }

      try {
        const userResponse = await backendApi.getCurrentUser();
        if (!userResponse.success || !userResponse.user?.id || isCancelled) return;

        const backendUser = userResponse.user;
        const resolvedId = resolveUserFilterId(user, backendUser);
        const loadUserId = (resolvedId ?? Number(backendUser.id)) || 0;

        const rows = await loadAllPaginatedData(
          (params) => databankService.loadByDateRange({
            userId: loadUserId,
            siteCode: siteCode,
            siteId: user?.siteId || backendUser?.site?.site_id || null,
            startDate: params.startDate,
            endDate: params.endDate,
            offset: params.offset,
            limit: params.limit
          }) as any,
          {
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            initialLimit: 200
          }
        );

        if (isCancelled) return;

        const loadedData: any = {};
        const loadedDates = new Set<string>();

        rows.forEach((row: DatabankDataRow) => {
          const normalizedICR = normalizeICR(row.icr);
          const normalizedInverter = normalizeInverter(row.inverter);

          if (!normalizedICR || !normalizedInverter || !ALL_ICRS.includes(normalizedICR)) return;
          const validInverters = getInvertersForICR(normalizedICR);
          if (!validInverters.includes(normalizedInverter)) return;

          loadedDates.add(row.entry_date);
          if (!loadedData[normalizedICR]) loadedData[normalizedICR] = {};
          if (!loadedData[normalizedICR][row.entry_date]) loadedData[normalizedICR][row.entry_date] = {};
          loadedData[normalizedICR][row.entry_date][normalizedInverter] =
            row.energy_kwh !== null && row.energy_kwh !== undefined
              ? parseFloat(row.energy_kwh.toString()).toFixed(3)
              : '';
        });

        setData(loadedData);
        setDates(Array.from(loadedDates).sort());
      } catch (error) {
        console.error('Error loading filtered databank data:', error);
      }
    };

    loadFilteredDatabankData();
    return () => {
      isCancelled = true;
    };
  }, [startDate, endDate, siteCode, user]);

  const previousStorageKeyRef = useRef(storageKey);

  useEffect(() => {
    if (previousStorageKeyRef.current === storageKey) return;
    previousStorageKeyRef.current = storageKey;
    const snapshot = readPersistedGrid(storageKey);
    // Only use persisted data if it has 20 or fewer dates
    if (snapshot.dates && snapshot.dates.length > 20) {
localStorage.removeItem(storageKey);
      setData({});
      setDates([]);
    } else {
      setData(snapshot.data || {});
      setDates(snapshot.dates || []);
    }
  }, [storageKey]);

  // Initialize originalDataRef when data is first populated (if not already set)
  // Use a ref to track if we've already initialized to prevent resetting on every data change
  // NOTE: This should only be a fallback - the main initialization should happen when data loads from backend
  const hasInitializedOriginalRef = useRef(false);
  useEffect(() => {
    const hasData = Object.keys(data).length > 0;
    const hasOriginalData = Object.keys(originalDataRef.current).length > 0;

    // Only initialize ONCE when we first get data, and never reset it
    // This is a fallback in case backend load didn't set it
    if (hasData && !hasOriginalData && !hasInitializedOriginalRef.current && isInitialLoadRef.current) {
      originalDataRef.current = JSON.parse(JSON.stringify(data));
      hasInitializedOriginalRef.current = true;
}
  }, [data]); // Remove isInitialLoad from dependencies

  // Auto-save Databank data to localStorage
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem(storageKey, JSON.stringify({
          data,
          dates,
          timestamp: Date.now()
        }));
      } catch (error) {
}
    }, 500); // Debounce 500ms

    return () => clearTimeout(timeoutId);
  }, [data, dates, storageKey]);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRefs = useRef<any>({});

  // ✅ Use smart hook with React Query - disabled data fetching to prevent showing backend data
  const {
    data: backendData,
    isLoading,
    isFetching,
    error,
    save,           // â† Single save
    saveBatch,      // â† Batch save (MUST exist in useFormData!)
    isSaving,
    refetch
  } = useFormData<DatabankDataRow>({
    siteCode: siteCode,
    endpoint: '/databank',
    dateRange: {
      start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      end: new Date().toISOString().split('T')[0]
    },
    enabled: false  // Disable automatic data loading - user only wants to insert, not view
  });

  // Memoized so it stays reference-stable across edits (only changes when the
  // condensed/expanded toggle actually changes) — DatabankIcrGroup's memoization
  // depends on this being stable.
  const styleVars = useMemo<DatabankStyleVars>(() => (
    isCondensed
      ? { h: 28, hh: 32, w: 120, icrW: 90, invW: 120, fs: 11, hfs: 10 }
      : { h: 38, hh: 44, w: 120, icrW: 120, invW: 120, fs: 14, hfs: 13 }
  ), [isCondensed]);

  // Column (date) virtualization: only date columns near the horizontal viewport are
  // mounted as real DOM cells, no matter how much history has been lazy-loaded via
  // left-scroll. Rows stay un-virtualized on purpose — each ICR's label cell uses a
  // native HTML `rowSpan` across its inverter rows, which requires every row it spans
  // to be physically present in the DOM; row count here is also naturally bounded
  // (~237 rows total for this site) while date columns grow unbounded with history,
  // so columns are the axis that actually needs windowing.
  const getColumnScrollElement = useCallback(() => containerRef.current, []);
  const estimateColumnSize = useCallback(() => styleVars.w, [styleVars.w]);
  const columnVirtualizer = useVirtualizer({
    count: filteredDates.length,
    getScrollElement: getColumnScrollElement,
    estimateSize: estimateColumnSize,
    horizontal: true,
    overscan: 6
  });
  const virtualColumns = columnVirtualizer.getVirtualItems();
  const columnsTotalSize = columnVirtualizer.getTotalSize();
  const columnPaddingLeft = virtualColumns.length > 0 ? virtualColumns[0].start : 0;
  const columnPaddingRight = virtualColumns.length > 0
    ? columnsTotalSize - virtualColumns[virtualColumns.length - 1].end
    : 0;

  // Date conversion utilities - Format: 23Nov25 (ddmmmyy)
  const formatDateToDisplay = (dateStr: string): string => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    const monthIndex = parseInt(month, 10) - 1;
    const monthAbbreviations = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthLabel = monthAbbreviations[Math.max(0, Math.min(11, monthIndex))] || month;
    // Format as ddmmmyy (e.g., 23Nov25)
    const twoDigitYear = year.slice(-2);
    return `${day}${monthLabel}${twoDigitYear}`;
  };

  // ✅ Disabled: Don't load data from backend - user only wants to insert, not view existing data
  // useEffect(() => {
  //   if (backendData && backendData.length > 0) {
  //     const loadedData: any = {};
  //     const loadedDates = new Set<string>();
  //     
  //     backendData.forEach((row: DatabankDataRow) => {
  //       loadedDates.add(row.entry_date);
  //       if (!loadedData[row.icr]) loadedData[row.icr] = {};
  //       if (!loadedData[row.icr][row.entry_date]) loadedData[row.icr][row.entry_date] = {};
  //       loadedData[row.icr][row.entry_date][row.inverter] = row.energy_kwh?.toString() || '';
  //     });
  //     
  //     setData(loadedData);
  //     setDates(Array.from(loadedDates).sort());
  //   } else if (!isLoading && backendData.length === 0 && dates.length === 0) {
  //     const today = new Date().toISOString().split('T')[0];
  //     setDates([today]);
  //   }
  // }, [backendData, isLoading]);

  // Fetch site name from database
  useEffect(() => {
    const fetchSiteName = async () => {
      try {
        const response = await backendApi.getSites(false, siteCode.toUpperCase());
        if (response.success && Array.isArray(response.sites)) {
          const matchedSite = response.sites.find(
            (item: any) => item.site_code && item.site_code.toUpperCase() === siteCode.toUpperCase()
          ) || response.sites[0];

          if (matchedSite && matchedSite.site_name) {
            setSiteName(matchedSite.site_name);
          } else {
            setSiteName(siteCode.toUpperCase());
          }
        }
      } catch (error) {
setSiteName(siteCode.toUpperCase());
      }
    };

    fetchSiteName();
  }, [siteCode]);

  // ✅ Initialize by loading user's existing data
  // IMPORTANT: Check for persisted unsaved data first to preserve user's work
  // Only load once - if data already exists, skip loading to prevent reloading when switching sheets
  useEffect(() => {
// Skip if we've already loaded data (prevents reloading when switching between sheets)
    if (!isInitialLoadRef.current || hasLoadedDataRef.current) {
return;
    }

    // Snapshot whatever's already in state (restored from localStorage — typically
    // unsaved edits from a previous session) so it can be merged back in below, but
    // ALWAYS proceed to fetch fresh from the backend regardless of how many dates are
    // already present. Previously, having <=15 dates already loaded skipped the
    // backend fetch entirely and trusted that snapshot forever — if that snapshot was
    // itself a partial/truncated load (e.g. from the pagination bug fixed alongside
    // this), the sheet would stay stuck on incomplete history with no way to recover
    // short of manually clearing localStorage.
    const hasExistingData = Object.keys(data).length > 0 && dates.length > 0;
    const localSnapshotData = hasExistingData ? data : null;
    const localSnapshotDates = hasExistingData ? dates : null;

const loadUserData = async () => {
      try {
        // First, check for persisted unsaved data (Databank uses different structure)
        // Only use persisted data if it has actual meaningful data (not just empty cells)
        const persistedData = localStorage.getItem(storageKey);

        if (persistedData) {
          try {
            const parsed = JSON.parse(persistedData);
            if (parsed && parsed.data) {
              // Check if persisted data has any meaningful values
              const hasData = Object.keys(parsed.data).some(icr => {
                const icrData = parsed.data[icr];
                if (!icrData) return false;
                return Object.keys(icrData).some(date => {
                  const dateData = icrData[date];
                  if (!dateData) return false;
                  return Object.keys(dateData).some(inverter => {
                    const value = dateData[inverter];
                    return value !== null && value !== undefined && value !== '' && value !== '0';
                  });
                });
              });

              if (hasData) {
                // If persisted data has more than 15 dates, clear it and load fresh from backend
                // This ensures we only load 15 days initially, then lazy load on scroll
                if (parsed.dates && parsed.dates.length > 15) {
localStorage.removeItem(storageKey);
                  // Continue to load from backend with 15-day limit
                } else {
                  // Persisted data is within limit, but still load from backend to ensure latest data
}
                // Don't return - continue to load from backend
                // The backend data will be merged with persisted data if needed
              } else {
                // Persisted data exists but is empty, clear it
localStorage.removeItem(storageKey);
                setData({});
                setDates([]);
              }
            }
          } catch (error) {
}
        } else {
}

        // Second, if no persisted data, load from backend
        // Get current user ID
        const userResponse = await backendApi.getCurrentUser();
        if (userResponse.success && userResponse.user?.id) {
          const userId = userResponse.user.id;
          const backendUser = userResponse.user;
          const loadAll = shouldLoadAllRecords(user, backendUser);

          // Load user's existing data with site context
const loadResult = await databankService.loadByUser({
            userId: loadAll ? 0 : userId,
            siteCode: siteCode,
            siteId: user?.siteId || backendUser?.site?.site_id || null
          });
// Store total count from database
          if (loadResult.ok && loadResult.data) {
            setTotalCountFromDB(loadResult.data.length);
          }
          if (loadResult.ok && loadResult.data && loadResult.data.length > 0) {
const loadedData: any = {};
            const loadedDates = new Set<string>();

            loadResult.data.forEach((row: DatabankDataRow) => {
              const normalizedICR = normalizeICR(row.icr);
              const normalizedInverter = normalizeInverter(row.inverter);

              // Only process if ICR and Inverter are valid and exist in mapping
              if (normalizedICR && normalizedInverter) {
                // Verify ICR exists in ALL_ICRS
                if (!ALL_ICRS.includes(normalizedICR)) {
return;
                }

                // Verify Inverter exists for this ICR
                const validInverters = getInvertersForICR(normalizedICR);
                if (!validInverters.includes(normalizedInverter)) {
return;
                }

                loadedDates.add(row.entry_date);
                if (!loadedData[normalizedICR]) loadedData[normalizedICR] = {};
                if (!loadedData[normalizedICR][row.entry_date]) loadedData[normalizedICR][row.entry_date] = {};
                // Store energy value, converting to string and handling null/undefined
                const energyValue = row.energy_kwh !== null && row.energy_kwh !== undefined
                  ? parseFloat(row.energy_kwh.toString()).toFixed(3)
                  : '';
                loadedData[normalizedICR][row.entry_date][normalizedInverter] = energyValue;
              } else {
}
            });

            // Debug: Log loaded data structure
            // Sort dates ascending (oldest to newest) so newest appears on the right
            const sortedDates = Array.from(loadedDates).sort((a, b) => a.localeCompare(b));
// Filter dates to only include those that have data
            // This prevents showing empty columns (like the first column issue)
            const datesWithData = sortedDates.filter(date => {
              return Object.keys(loadedData).some(icr => {
                const dateData = loadedData[icr]?.[date];
                return dateData && Object.keys(dateData).length > 0;
              });
            });

            if (datesWithData.length !== sortedDates.length) {
              const emptyDates = sortedDates.filter(date => !datesWithData.includes(date));
}

            // Check if first date has data
            if (datesWithData.length > 0) {
              const firstDate = datesWithData[0];
}

            // Use dates with data, or fallback to all dates if filtering removes everything
            // Ensure dates are sorted ascending (oldest to newest) so newest appears on the right
            let finalDates = (datesWithData.length > 0 ? datesWithData : sortedDates).sort((a, b) => a.localeCompare(b));

            // Show the most recent INITIAL_LOAD_DAYS dates immediately on page load;
            // older history loads lazily as the user scrolls left.
            const INITIAL_LOAD_DAYS = 20;
            let dataToSet = loadedData;
            if (finalDates.length > INITIAL_LOAD_DAYS) {
              // Keep the newest INITIAL_LOAD_DAYS dates (last N in sorted array)
              finalDates = finalDates.slice(-INITIAL_LOAD_DAYS);

              // Also filter loadedData to only include these dates
              const filteredData: any = {};
              Object.keys(loadedData).forEach(icr => {
                filteredData[icr] = {};
                finalDates.forEach(date => {
                  if (loadedData[icr]?.[date]) {
                    filteredData[icr][date] = loadedData[icr][date];
                  }
                });
              });
              dataToSet = filteredData;
            }

            // Merge in any locally-persisted unsaved edits that aren't already covered
            // by this fresh backend response (backend values always win per-cell, since
            // they're the saved source of truth).
            if (localSnapshotData && localSnapshotDates && localSnapshotDates.length > 0) {
              Object.keys(localSnapshotData).forEach(icr => {
                if (!dataToSet[icr]) dataToSet[icr] = {};
                Object.keys(localSnapshotData[icr]).forEach(date => {
                  if (!dataToSet[icr][date]) dataToSet[icr][date] = {};
                  Object.keys(localSnapshotData[icr][date]).forEach(inv => {
                    if (dataToSet[icr][date][inv] === undefined) {
                      dataToSet[icr][date][inv] = localSnapshotData[icr][date][inv];
                    }
                  });
                });
              });
              finalDates = Array.from(new Set([...finalDates, ...localSnapshotDates])).sort((a, b) => a.localeCompare(b));
            }

            setData(dataToSet);
            // Store original loaded data for comparison (deep copy)
            // IMPORTANT: This must be set when data is loaded from backend
            originalDataRef.current = JSON.parse(JSON.stringify(dataToSet));
            hasInitializedOriginalRef.current = true;

            // ✅ Clear tracked changes when loading fresh data from backend
            changedEntriesRef.current.clear();
setDates(finalDates);

            // Track the oldest loaded date for lazy loading
            if (finalDates.length > 0) {
              setOldestLoadedDate(finalDates[0]); // Oldest date (first in sorted array)
              setHasMoreData(true); // Assume there's more data until proven otherwise
            }

            // Mark that we've loaded data to prevent reloading when switching sheets
            hasLoadedDataRef.current = true;
// If we got less than 15 dates, log a warning
            if (finalDates.length < 15 && sortedDates.length < 15) {
}

            // Initial scroll to right will be handled by useEffect
            // Don't scroll here to avoid conflicts with scroll preservation
          } else {
            // No existing data, create empty row for today
            const today = new Date().toISOString().split('T')[0];
            setDates([today]);
            hasLoadedDataRef.current = true; // Mark as loaded even if no data
}
        } else {
          // User not found, create empty row for today
          const today = new Date().toISOString().split('T')[0];
          setDates([today]);
          hasLoadedDataRef.current = true; // Mark as loaded even if no user
}
      } catch (error: any) {
        console.error('âŒ [INITIAL LOAD] Error loading user data:', error);
        // On error, create empty row for today
        const today = new Date().toISOString().split('T')[0];
        setDates([today]);
        hasLoadedDataRef.current = true; // Mark as loaded even on error
      } finally {
        isInitialLoadRef.current = false; // Mark initial load as complete
        setIsInitialLoad(false);
}
    };

    // Always call loadUserData if we reach this point
    loadUserData().catch((error) => {
      console.error('âŒ [INITIAL LOAD] Failed to load user data:', error);
      // On error, create empty row for today
      const today = new Date().toISOString().split('T')[0];
      setDates([today]);
      hasLoadedDataRef.current = true;
      isInitialLoadRef.current = false;
      setIsInitialLoad(false);
    });
  }, [storageKey, siteCode]); // Remove isInitialLoad from dependencies, use ref instead

  // Force ICR and Inverter header text to be bold - override any CSS rules
  useEffect(() => {
    const forceBoldHeaders = () => {
      // Find all ICR and Inverter header elements
      const icrHeaders = document.querySelectorAll('.sticky-header-icr, .sticky-header-icr span');
      const invHeaders = document.querySelectorAll('.sticky-header-inv, .sticky-header-inv span');

      [...icrHeaders, ...invHeaders].forEach((el) => {
        if (el instanceof HTMLElement) {
          el.style.setProperty('font-weight', '700', 'important');
          el.style.setProperty('font-family', "'IBM Plex Sans', sans-serif", 'important');
        }
      });
    };

    // Run immediately and after a short delay to catch dynamically rendered elements
    forceBoldHeaders();
    const timeoutId = setTimeout(forceBoldHeaders, 100);
    const timeoutId2 = setTimeout(forceBoldHeaders, 500);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(timeoutId2);
    };
  }, [dates, isCondensed]);

  // Preserve horizontal scroll position when dates change (for lazy loading).
  // useLayoutEffect fires synchronously after DOM mutations, before browser paint — no visible jump.
  useLayoutEffect(() => {
    if (dates.length > 0 && containerRef.current && scrollPositionRef.current !== null) {
      const targetScroll = scrollPositionRef.current;
      scrollPositionRef.current = null;
      containerRef.current.style.scrollBehavior = 'auto';
      containerRef.current.scrollLeft = targetScroll;
}
  }, [dates.length]);

  // Auto-scroll to rightmost column (newest date) ONLY on initial load.
  // useLayoutEffect ensures this fires after the table DOM is committed.
  useLayoutEffect(() => {
    if (dates.length > 0 && containerRef.current && !isInitialScrollDone && scrollPositionRef.current === null) {
      const el = containerRef.current;
      if (el.scrollWidth > el.clientWidth) {
        el.style.scrollBehavior = 'auto';
        el.scrollLeft = el.scrollWidth - el.clientWidth;
        setIsInitialScrollDone(true);
}
    }
  }, [dates.length, isInitialScrollDone]);

  // Function to load more older data
  const loadMoreOlderData = async (currentOldestDate: string) => {
    try {
// Calculate date range: 30 days before current oldest date
      const currentOldest = new Date(currentOldestDate);
      const newEndDate = new Date(currentOldest);
      newEndDate.setDate(newEndDate.getDate() - 1); // One day before current oldest
      const newStartDate = new Date(newEndDate);
      newStartDate.setDate(newStartDate.getDate() - 30); // 30 days before

      const start = newStartDate.toISOString().split('T')[0];
      const end = newEndDate.toISOString().split('T')[0];

      // Get user context
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) {
        setIsLoadingMore(false);
        return;
      }

      const userId = userResponse.user.id;
      const backendUser = userResponse.user;
      const loadAll = shouldLoadAllRecords(user, backendUser);

      // Load data for the older date range. Databank is ~200 rows/day (one per ICR x
      // inverter), so a 30-day window is easily thousands of rows — page through it
      // (same pattern as the date-filter load) instead of one capped call.
      const rows = await loadAllPaginatedData(
        (params) => databankService.loadByDateRange({
          userId: loadAll ? 0 : userId,
          siteCode: siteCode,
          siteId: user?.siteId || backendUser?.site?.site_id || null,
          startDate: params.startDate,
          endDate: params.endDate,
          offset: params.offset,
          limit: params.limit
        }) as any,
        { startDate: start, endDate: end, initialLimit: 400 }
      );

      if (rows.length > 0) {
        const newData: any = {};
        const newDates = new Set<string>();

        rows.forEach((row: DatabankDataRow) => {
          const normalizedICR = normalizeICR(row.icr);
          const normalizedInverter = normalizeInverter(row.inverter);

          if (normalizedICR && normalizedInverter && ALL_ICRS.includes(normalizedICR)) {
            const validInverters = getInvertersForICR(normalizedICR);
            if (validInverters.includes(normalizedInverter)) {
              newDates.add(row.entry_date);
              if (!newData[normalizedICR]) newData[normalizedICR] = {};
              if (!newData[normalizedICR][row.entry_date]) newData[normalizedICR][row.entry_date] = {};
              const energyValue = row.energy_kwh !== null && row.energy_kwh !== undefined
                ? parseFloat(row.energy_kwh.toString()).toFixed(3)
                : '';
              newData[normalizedICR][row.entry_date][normalizedInverter] = energyValue;
            }
          }
        });

        // Merge new data with existing data smoothly
        setData((prevData: any) => {
          const merged = { ...prevData };
          Object.keys(newData).forEach(icr => {
            if (!merged[icr]) merged[icr] = {};
            Object.keys(newData[icr]).forEach(date => {
              if (!merged[icr][date]) merged[icr][date] = {};
              Object.assign(merged[icr][date], newData[icr][date]);
            });
          });
          return merged;
        });

        // Add new dates to the beginning (they're older) and maintain sort
        const sortedNewDates = Array.from(newDates).sort((a, b) => a.localeCompare(b));
        if (sortedNewDates.length > 0) {
          // Save current scroll position and width BEFORE updating dates
          const currentScrollLeft = containerRef.current?.scrollLeft || 0;
          const currentScrollWidth = containerRef.current?.scrollWidth || 0;

          // Calculate the actual width per column based on current table
          const currentDateCount = dates.length;
          const widthPerColumn = currentDateCount > 0 ? currentScrollWidth / currentDateCount : 100;

          // Estimate width of new columns more accurately
          const estimatedNewWidth = sortedNewDates.length * widthPerColumn;

          // Save scroll position to restore after DOM update
          // Add the estimated width to maintain visual position
          scrollPositionRef.current = currentScrollLeft + estimatedNewWidth;
setDates((prevDates) => {
            const merged = [...sortedNewDates, ...prevDates].sort((a, b) => a.localeCompare(b));
            return merged;
          });

          // Update oldest loaded date
          setOldestLoadedDate(sortedNewDates[0]);
} else {
          // No more data available
          setHasMoreData(false);
}
      } else {
        // No more data available
        setHasMoreData(false);
}
    } catch (error: any) {
      console.error('âŒ Error loading more data:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Lazy loading: Load more older data when user scrolls left
  useEffect(() => {
    // Disable lazy loading when date filter is active
    if (startDate || endDate) {
      return;
    }

    const container = containerRef.current;
    if (!container || !oldestLoadedDate || isLoadingMore || !hasMoreData) {
return;
    }

    let scrollTimeout: NodeJS.Timeout;
    let lastScrollLeft = container.scrollLeft;
    let isLoading = false;
    let checkInterval: NodeJS.Timeout;

    const checkAndLoad = () => {
      if (isLoading || isLoadingMore) {
        return;
      }

      const currentScrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      const scrollFromLeft = currentScrollLeft;
      const maxScroll = Math.max(0, scrollWidth - clientWidth);
      const scrollPercentFromLeft = maxScroll > 0 ? scrollFromLeft / maxScroll : 0;

      // Trigger loading if near left edge (less than 500px or less than 30% from left)
      const shouldLoad = (scrollFromLeft < 500 || scrollPercentFromLeft < 0.3) && scrollFromLeft >= 0;

      if (shouldLoad) {
isLoading = true;
        setIsLoadingMore(true);

        // Load 15 more days before the oldest loaded date
        loadMoreOlderData(oldestLoadedDate).finally(() => {
          isLoading = false;
        });
      }
    };

    const handleScroll = () => {
      // Clear previous timeout
      clearTimeout(scrollTimeout);

      const currentScrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;

      // Check if user is scrolling left (towards older dates)
      const isScrollingLeft = currentScrollLeft < lastScrollLeft;
      lastScrollLeft = currentScrollLeft;

      // Calculate scroll position from left edge
      const scrollFromLeft = currentScrollLeft;
      const maxScroll = Math.max(0, scrollWidth - clientWidth);
      const scrollPercentFromLeft = maxScroll > 0 ? scrollFromLeft / maxScroll : 0;

      // Debounce scroll events - use requestAnimationFrame for smooth performance
      scrollTimeout = setTimeout(() => {
        checkAndLoad();
      }, 300); // Increased debounce to reduce lag
    };

    // Check immediately on mount and periodically
    // Use requestAnimationFrame to avoid blocking UI
    requestAnimationFrame(() => {
      checkAndLoad();
    });

    // Also check periodically (every 3 seconds) in case user is already at left edge
    // Increased interval to reduce performance impact
    checkInterval = setInterval(() => {
      if (!isLoading && !isLoadingMore) {
        requestAnimationFrame(() => {
          checkAndLoad();
        });
      }
    }, 3000);
container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
      clearInterval(checkInterval);
    };
  }, [oldestLoadedDate, isLoadingMore, hasMoreData, startDate, endDate]);

  // Data operations
  const updateCell = (icr: string, date: string, inverter: string, value: string) => {
    // Update the data state
    setData((prev: any) => ({
      ...prev,
      [icr]: { ...prev[icr], [date]: { ...prev[icr]?.[date], [inverter]: value } }
    }));

    // ✅ Track this change - only if user actually entered a value
    if (value && value.toString().trim() !== '') {
      const numValue = parseFloat(value.toString().trim());
      if (!isNaN(numValue) && isFinite(numValue)) {
        const key = `${date}|${icr}|${inverter}`;
        const normalizedValue = parseFloat(numValue.toFixed(3));

        // Get original value to determine if this is new or changed
        const originalValue = originalDataRef.current[icr]?.[date]?.[inverter];
        const hasOriginal = originalValue !== undefined && originalValue !== null && originalValue.toString().trim() !== '';

        // Check if value actually changed (for existing entries)
        let isActuallyChanged = true;
        if (hasOriginal) {
          const originalNum = parseFloat(originalValue.toString().trim());
          if (!isNaN(originalNum) && isFinite(originalNum)) {
            const normalizedOriginal = parseFloat(originalNum.toFixed(3));
            const diff = Math.abs(normalizedOriginal - normalizedValue);
            isActuallyChanged = diff > 0.0001; // Only track if actually changed
          }
        }

        // Only track if it's new or actually changed
        if (!hasOriginal || isActuallyChanged) {
          changedEntriesRef.current.set(key, {
            entry_date: date,
            icr: icr,
            inverter: inverter,
            energy_kwh: normalizedValue,
            site_code: siteCode
          });
} else {
          // Value is same as original, remove from tracked changes if it was there
          if (changedEntriesRef.current.has(key)) {
            changedEntriesRef.current.delete(key);
}
        }
      }
    } else {
      // User cleared the value - check if it was in original data
      const key = `${date}|${icr}|${inverter}`;
      const originalValue = originalDataRef.current[icr]?.[date]?.[inverter];
      const hasOriginal = originalValue !== undefined && originalValue !== null && originalValue.toString().trim() !== '';

      if (hasOriginal) {
        // User cleared a value that existed - this is a change (setting to empty/null)
        // Note: We might want to handle this differently, but for now we'll track it
        changedEntriesRef.current.delete(key); // Remove from tracked changes if clearing
} else {
        // User cleared a value that didn't exist - no change needed
        changedEntriesRef.current.delete(key);
      }
    }
  };

  const addDate = async () => {
    let newDate: string;

    if (dates.length > 0) {
      // Sort dates ascending to get the latest date (newest is last)
      const sortedDates = [...dates].sort((a, b) => a.localeCompare(b));
      const latestDate = sortedDates[sortedDates.length - 1];
      const nextDay = new Date(latestDate);
      nextDay.setDate(nextDay.getDate() + 1);
      newDate = nextDay.toISOString().split('T')[0];
    } else {
      newDate = new Date().toISOString().split('T')[0];
    }

    if (!dates.includes(newDate)) {
      // Sort dates ascending (oldest to newest) so newest appears on the right
      setDates(prev => [...prev, newDate].sort((a, b) => a.localeCompare(b)));

      // Load data for the new date if it exists in the database
      try {
        const userResponse = await backendApi.getCurrentUser();
        if (userResponse.success && userResponse.user?.id) {
          const userId = userResponse.user.id;
          const backendUser = userResponse.user;
          const loadAll = shouldLoadAllRecords(user, backendUser);

          // Use loadByDateRange to load data for the specific date
          const loadResult = await databankService.loadByDateRange({
            userId: loadAll ? 0 : userId,
            siteCode: siteCode,
            siteId: user?.siteId || backendUser?.site?.site_id || null,
            startDate: newDate,
            endDate: newDate
          });

          if (loadResult.ok && loadResult.data && loadResult.data.length > 0) {
            const newData = { ...data };
            loadResult.data.forEach((row: DatabankDataRow) => {
              const normalizedICR = normalizeICR(row.icr);
              const normalizedInverter = normalizeInverter(row.inverter);

              if (normalizedICR && normalizedInverter && ALL_ICRS.includes(normalizedICR)) {
                const validInverters = getInvertersForICR(normalizedICR);
                if (validInverters.includes(normalizedInverter)) {
                  if (!newData[normalizedICR]) newData[normalizedICR] = {};
                  if (!newData[normalizedICR][newDate]) newData[normalizedICR][newDate] = {};
                  const energyValue = row.energy_kwh !== null && row.energy_kwh !== undefined
                    ? parseFloat(row.energy_kwh.toString()).toFixed(3)
                    : '';
                  newData[normalizedICR][newDate][normalizedInverter] = energyValue;
                }
              }
            });
            setData(newData);
            // Update original data ref
            Object.keys(newData).forEach(icr => {
              if (newData[icr]?.[newDate]) {
                if (!originalDataRef.current[icr]) originalDataRef.current[icr] = {};
                originalDataRef.current[icr][newDate] = { ...newData[icr][newDate] };
              }
            });
}
        }
      } catch (error) {
}

      // Set scroll position to the new date (rightmost column) immediately
      requestAnimationFrame(() => {
        if (containerRef.current) {
          const scrollWidth = containerRef.current.scrollWidth;
          const clientWidth = containerRef.current.clientWidth;
          if (scrollWidth > clientWidth) {
            containerRef.current.scrollLeft = scrollWidth - clientWidth;
          }
        }
      });
    }
  };

  const updateDate = async (idx: number, newDateISO: string) => {
    const oldDate = dates[idx];
    const newDates = [...dates];
    newDates[idx] = newDateISO;
    // Sort dates ascending (oldest to newest) so newest appears on the right
    setDates(newDates.sort((a, b) => a.localeCompare(b)));

    const newData = { ...data };
    ALL_ICRS.forEach(icr => {
      if (newData[icr]?.[oldDate]) {
        newData[icr][newDateISO] = newData[icr][oldDate];
        delete newData[icr][oldDate];
      }
    });
    setData(newData);

    // Load data for the new date if it exists in the database and we don't already have it
    if (!newData[Object.keys(newData)[0]]?.[newDateISO]) {
      try {
        const userResponse = await backendApi.getCurrentUser();
        if (userResponse.success && userResponse.user?.id) {
          const userId = userResponse.user.id;
          const backendUser = userResponse.user;
          const loadAll = shouldLoadAllRecords(user, backendUser);

          // Use loadByDateRange to load data for the specific date
          const loadResult = await databankService.loadByDateRange({
            userId: loadAll ? 0 : userId,
            siteCode: siteCode,
            siteId: user?.siteId || backendUser?.site?.site_id || null,
            startDate: newDateISO,
            endDate: newDateISO
          });

          if (loadResult.ok && loadResult.data && loadResult.data.length > 0) {
            loadResult.data.forEach((row: DatabankDataRow) => {
              const normalizedICR = normalizeICR(row.icr);
              const normalizedInverter = normalizeInverter(row.inverter);

              if (normalizedICR && normalizedInverter && ALL_ICRS.includes(normalizedICR)) {
                const validInverters = getInvertersForICR(normalizedICR);
                if (validInverters.includes(normalizedInverter)) {
                  if (!newData[normalizedICR]) newData[normalizedICR] = {};
                  if (!newData[normalizedICR][newDateISO]) newData[normalizedICR][newDateISO] = {};
                  const energyValue = row.energy_kwh !== null && row.energy_kwh !== undefined
                    ? parseFloat(row.energy_kwh.toString()).toFixed(3)
                    : '';
                  newData[normalizedICR][newDateISO][normalizedInverter] = energyValue;
                }
              }
            });
            setData(newData);
            // Update original data ref
            Object.keys(newData).forEach(icr => {
              if (newData[icr]?.[newDateISO]) {
                if (!originalDataRef.current[icr]) originalDataRef.current[icr] = {};
                originalDataRef.current[icr][newDateISO] = { ...newData[icr][newDateISO] };
              }
            });
}
        }
      } catch (error) {
}
    }
  };

  const deleteDate = (idx: number) => {
    const dateToDelete = dates[idx];
    const newDates = dates.filter((_, i) => i !== idx);
    setDates(newDates);

    const newData = { ...data };
    ALL_ICRS.forEach(icr => {
      if (newData[icr]?.[dateToDelete]) {
        delete newData[icr][dateToDelete];
      }
    });
    setData(newData);

    if (focusedCell.colIndex >= newDates.length) {
      setFocusedCell(prev => ({ ...prev, colIndex: Math.max(0, newDates.length - 1) }));
    }
  };

  const calculateGrandTotal = (date: string): number => {
    return ALL_ICRS.reduce((sum, icr) => {
      return sum + calculateICRSum(icr, date, data);
    }, 0);
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent, icrIdx: number, colIdx: number, invIdx: number) => {
    if ((e.key === 'Delete' || e.key === 'Backspace') && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      const icr = ALL_ICRS[icrIdx], inv = getInvertersForICR(icr)[invIdx], date = dates[colIdx];
      updateCell(icr, date, inv, '');
      return;
    }

    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Tab'].includes(e.key)) {
      e.preventDefault();
      let newRow = icrIdx, newCol = colIdx, newInv = invIdx;
      const invs = getInvertersForICR(ALL_ICRS[icrIdx]);

      if (e.key === 'ArrowUp') {
        if (invIdx > 0) newInv = invIdx - 1;
        else if (icrIdx > 0) { newRow = icrIdx - 1; newInv = getInvertersForICR(ALL_ICRS[newRow]).length - 1; }
      } else if (e.key === 'ArrowDown' || e.key === 'Enter') {
        if (invIdx < invs.length - 1) newInv = invIdx + 1;
        else if (icrIdx < ALL_ICRS.length - 1) { newRow = icrIdx + 1; newInv = 0; }
      } else if (e.key === 'ArrowLeft') {
        newCol = Math.max(0, colIdx - 1);
      } else if (e.key === 'ArrowRight' || e.key === 'Tab') {
        newCol = Math.min(dates.length - 1, colIdx + 1);
      }

      setFocusedCell({ rowIndex: newRow, colIndex: newCol, inverterIndex: newInv });
      // If the target column isn't currently mounted (column virtualization only
      // renders columns near the viewport), scroll it into view first so the input
      // actually exists in the DOM by the time we try to focus it below.
      if (newCol !== colIdx) {
        columnVirtualizer.scrollToIndex(newCol, { align: 'auto' });
      }
      setTimeout(() => {
        const key = `${newRow}:${newCol}:${newInv}`;
        inputRefs.current[key]?.focus();
        inputRefs.current[key]?.select();
      }, 50);
    }
  };

  // ✅ Save only user-edited/changed entries
  const handleSave = async () => {
    try {
// ✅ Get all tracked changes (only what user actually edited)
      const trackedChanges = Array.from(changedEntriesRef.current.values());
if (trackedChanges.length === 0) {
        setStatus({ type: 'warning', message: `⚠️ No changes to save. You haven't edited any values.` });
return;
      }

      // Convert tracked changes to rows array
      const rows: DatabankDataRow[] = trackedChanges;

      // Count new vs changed for summary
      let newCount = 0;
      let changedCount = 0;
      const originalData = originalDataRef.current;

      trackedChanges.forEach(row => {
        const originalValue = originalData[row.icr]?.[row.entry_date]?.[row.inverter];
        const hasOriginal = originalValue !== undefined && originalValue !== null && originalValue.toString().trim() !== '';
        if (hasOriginal) {
          changedCount++;
        } else {
          newCount++;
        }
      });
// Validate we have entries to save
      if (rows.length === 0) {
        setStatus({ type: 'warning', message: `⚠️ No changes to save. You haven't edited any values.` });
        return;
      }
// Chunk rows into smaller batches to prevent timeout
      const BATCH_SIZE = 100; // Save 100 rows at a time
      const chunks: DatabankDataRow[][] = [];
      // Split tracked changes into batches
      for (let i = 0; i < rows.length; i += BATCH_SIZE) {
        chunks.push(rows.slice(i, i + BATCH_SIZE));
      }
let totalSaved = 0;
      let totalFailed = 0;
      const errors: string[] = [];

      // Save batches sequentially to avoid overwhelming the server
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        try {
setStatus({
            type: 'info',
            message: `ðŸ’¾ Saving batch ${i + 1}/${chunks.length} (${totalSaved + chunk.length}/${rows.length} rows)...`
          });

          await saveBatch(chunk);
          totalSaved += chunk.length;
} catch (error: any) {
          totalFailed += chunk.length;
          const errorMsg = `Batch ${i + 1}: ${error.message || 'Unknown error'}`;
          errors.push(errorMsg);
          console.error(`âŒ Batch ${i + 1}/${chunks.length} failed:`, error);

          // Continue with next batch even if one fails
          if (i < chunks.length - 1) {
}
        }
      }

      if (totalFailed > 0) {
        throw new Error(`Failed to save ${totalFailed} row(s). ${errors.join('; ')}`);
      }

      // Reload saved data filtered by user ID to display in table
      const userResponse = await backendApi.getCurrentUser();
      if (userResponse.success && userResponse.user?.id) {
        const backendUser = userResponse.user;
        const userIdForLoad = resolveUserFilterId(user, backendUser);
        if (userIdForLoad === null) {
}
        const fallbackUserId = (userIdForLoad ?? Number(backendUser.id)) || 0;
        const loadResult = await databankService.loadByUser({
          userId: fallbackUserId,
          siteCode: siteCode,
          siteId: user?.siteId || backendUser?.site?.site_id || null
        });
        if (loadResult.ok && loadResult.data && loadResult.data.length > 0) {
          // Filter to only include dates that are in the current dates array
          const savedDates = new Set(dates);
          const savedData = loadResult.data.filter((row: DatabankDataRow) =>
            savedDates.has(row.entry_date)
          );

          const loadedData: any = {};
          const loadedDates = new Set<string>();

          savedData.forEach((row: DatabankDataRow) => {
            const normalizedICR = normalizeICR(row.icr);
            const normalizedInverter = normalizeInverter(row.inverter);

            // Verify ICR and Inverter are valid
            if (normalizedICR && normalizedInverter && ALL_ICRS.includes(normalizedICR)) {
              const validInverters = getInvertersForICR(normalizedICR);
              if (validInverters.includes(normalizedInverter)) {
                loadedDates.add(row.entry_date);
                if (!loadedData[normalizedICR]) loadedData[normalizedICR] = {};
                if (!loadedData[normalizedICR][row.entry_date]) loadedData[normalizedICR][row.entry_date] = {};
                const energyValue = row.energy_kwh !== null && row.energy_kwh !== undefined
                  ? parseFloat(row.energy_kwh.toString()).toFixed(3)
                  : '';
                loadedData[normalizedICR][row.entry_date][normalizedInverter] = energyValue;
              }
            }
          });

          // Merge with existing data
          const mergedData = { ...data };
          Object.keys(loadedData).forEach(icr => {
            if (!mergedData[icr]) mergedData[icr] = {};
            Object.keys(loadedData[icr]).forEach(date => {
              if (!mergedData[icr][date]) mergedData[icr][date] = {};
              Object.assign(mergedData[icr][date], loadedData[icr][date]);
            });
          });

          setData(mergedData);
          const updatedDates = Array.from(new Set([...dates, ...Array.from(loadedDates)])).sort();
          setDates(updatedDates);

          // Update original data snapshot after successful save to reflect new saved state
          // This ensures future saves only detect new changes
          originalDataRef.current = JSON.parse(JSON.stringify(mergedData));
          hasInitializedOriginalRef.current = true; // Mark as initialized

          // ✅ Clear tracked changes after successful save
          changedEntriesRef.current.clear();
}
      }

      // Clear unsaved data from localStorage after successful save
      try {
        localStorage.removeItem(storageKey);
      } catch (error) {
}

      // Calculate change summary for success message
      const changeSummary = newCount > 0 && changedCount > 0
        ? `${newCount} new, ${changedCount} updated`
        : newCount > 0
          ? `${newCount} new`
          : `${changedCount} updated`;

      setStatus({ type: 'success', message: `✅ Successfully saved ${totalSaved} record(s) (${changeSummary})!` });

    } catch (e: any) {
      console.error('âŒ Save error:', e);
      setStatus({ type: 'error', message: `âŒ ${e.message}` });
    }
  };

  // ✅ FIXED fetchSummary
  const fetchSummary = async () => {
    try {
      // backendData already has all loaded data from React Query
      // No need to fetch again, just show the modal
      setShowSummary(true);
    } catch (error: any) {
      console.error('Error showing summary:', error);
      setStatus({ type: 'error', message: `âŒ ${error.message}` });
    }
  };

  /**
   * Export data to CSV
   */
  const exportData = async () => {
    setIsExporting(true);

    try {
      // First save the data
      await handleSave();

      // Then export using the service
      const result = await databankService.export(dates);

      if (result.ok) {
        setStatus({ type: 'success', message: '✅ Export completed successfully!' });
      } else {
        setStatus({ type: 'error', message: `âŒ Export failed: ${result.error}` });
      }
    } catch (error: any) {
      console.error('âŒ Export error:', error);
      setStatus({ type: 'error', message: `âŒ Export failed: ${error.message}` });
    } finally {
      setIsExporting(false);
    }
  };

  // Column definition for Correct Mistake Modal
  const DATABANK_COLUMNS: ColumnDefinition[] = [
    { key: 'entry_date', label: 'Date', type: 'date', required: true },
    { key: 'icr', label: 'ICR', type: 'text', required: true },
    { key: 'inverter', label: 'Inverter', type: 'text', required: true },
    { key: 'energy_kwh', label: 'Energy (kWh)', type: 'number', required: true, suffix: ' kWh' }
  ];

  // Fetch user's data for Correct Mistake - Load initial batch (last 30 days)
  const handleCorrectMistake = async () => {
    setLoadingCorrectMistake(true);
    setCorrectMistakeHasMore(true);
    setCorrectMistakeLoadedRanges([]);
    correctMistakeOldestDateRef.current = null;

    try {
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) {
        setStatus({ type: 'error', message: 'Unable to identify user. Please login again.' });
        return;
      }

      const backendUser = userResponse.user;
      const resolvedId = resolveUserFilterId(user, backendUser);
      const loadUserId = (resolvedId ?? Number(backendUser.id)) || 0;

      // Load initial batch: last 30 days
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      const start = startDate.toISOString().split('T')[0];
      const end = endDate.toISOString().split('T')[0];

      const response = await databankService.loadByDateRange({
        userId: loadUserId,
        siteCode: siteCode,
        siteId: user?.siteId || backendUser?.site?.site_id || null,
        startDate: start,
        endDate: end
      });

      if (response.ok && response.data) {
        // Ensure ID is preserved in the data
        const dataWithIds = response.data.map((row: any) => ({
          ...row,
          id: row.id || row.pk || undefined
        }));
// Find oldest date in this batch
        if (dataWithIds.length > 0) {
          const dates = dataWithIds.map((r: any) => r.entry_date).filter(Boolean).sort();
          if (dates.length > 0) {
            correctMistakeOldestDateRef.current = dates[0];
          }
        }

        setCorrectMistakeData(dataWithIds);
        setCorrectMistakeLoadedRanges([{ start, end }]);

        // If we got less than expected, might not have more data
        if (dataWithIds.length < 100) {
          setCorrectMistakeHasMore(false);
        }

        setShowCorrectMistake(true);
      } else {
        setStatus({ type: 'error', message: `Failed to load data: ${response.error}` });
      }
    } catch (error: any) {
      setStatus({ type: 'error', message: `Error: ${error.message}` });
    } finally {
      setLoadingCorrectMistake(false);
    }
  };

  // Load more data for Correct Mistake modal (load next 30 days going backwards)
  const loadMoreCorrectMistakeData = async () => {
    if (correctMistakeLoadingMore || !correctMistakeHasMore || !correctMistakeOldestDateRef.current) {
      return;
    }

    setCorrectMistakeLoadingMore(true);
    try {
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) {
        return;
      }

      const backendUser = userResponse.user;
      const resolvedId = resolveUserFilterId(user, backendUser);
      const loadUserId = (resolvedId ?? Number(backendUser.id)) || 0;

      // Load next batch: 30 days before the oldest loaded date
      const oldestDate = new Date(correctMistakeOldestDateRef.current);
      const endDate = new Date(oldestDate);
      endDate.setDate(endDate.getDate() - 1); // Day before oldest
      const startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - 30); // 30 days before

      const start = startDate.toISOString().split('T')[0];
      const end = endDate.toISOString().split('T')[0];

      // Check if we've already loaded this range
      const alreadyLoaded = correctMistakeLoadedRanges.some(
        range => range.start === start && range.end === end
      );
      if (alreadyLoaded) {
        setCorrectMistakeHasMore(false);
        return;
      }

      const response = await databankService.loadByDateRange({
        userId: loadUserId,
        siteCode: siteCode,
        siteId: user?.siteId || backendUser?.site?.site_id || null,
        startDate: start,
        endDate: end
      });

      if (response.ok && response.data) {
        const dataWithIds = response.data.map((row: any) => ({
          ...row,
          id: row.id || row.pk || undefined
        }));
if (dataWithIds.length > 0) {
          // Update oldest date
          const dates = dataWithIds.map((r: any) => r.entry_date).filter(Boolean).sort();
          if (dates.length > 0) {
            const newOldest = dates[0];
            if (!correctMistakeOldestDateRef.current || newOldest < correctMistakeOldestDateRef.current) {
              correctMistakeOldestDateRef.current = newOldest;
            }
          }

          // Merge with existing data (prepend older data)
          setCorrectMistakeData(prev => [...dataWithIds, ...prev]);
          setCorrectMistakeLoadedRanges(prev => [...prev, { start, end }]);

          // If we got less than expected, might not have more data
          if (dataWithIds.length < 50) {
            setCorrectMistakeHasMore(false);
          }
        } else {
          // No more data
          setCorrectMistakeHasMore(false);
        }
      } else {
        setCorrectMistakeHasMore(false);
      }
    } catch (error: any) {
      console.error('Error loading more data:', error);
      setCorrectMistakeHasMore(false);
    } finally {
      setCorrectMistakeLoadingMore(false);
    }
  };

  // Save corrected data - update all rows that have IDs
  const handleSaveCorrectedData = async (updatedData: any[]) => {
    try {
      if (updatedData.length === 0) {
        throw new Error('No data to save');
      }

      const results: { success: number; failed: number; errors: string[] } = {
        success: 0,
        failed: 0,
        errors: []
      };

      // Process all rows that have IDs
      for (const row of updatedData) {
        if (row.id && typeof row.id === 'number') {
          try {
            // Parse energy_kwh properly - handle string, number, or null
            let energyKwh: number = 0;
            if (row.energy_kwh !== null && row.energy_kwh !== undefined && row.energy_kwh !== '') {
              const parsed = parseFloat(String(row.energy_kwh));
              if (!isNaN(parsed)) {
                energyKwh = parsed;
              }
            }

            const backendData: DatabankDataRow = {
              id: row.id,
              entry_date: row.entry_date,
              icr: String(row.icr || '').trim(),
              inverter: String(row.inverter || '').trim(),
              energy_kwh: energyKwh,
              site_code: siteCode
            };

            const updateResult = await databankService.updateById(row.id, backendData);
            if (updateResult.ok) {
              results.success++;
            } else {
              results.failed++;
              results.errors.push(`Row ${row.id} (${backendData.entry_date}): ${updateResult.error || 'Update failed'}`);
            }
          } catch (error: any) {
            results.failed++;
            results.errors.push(`Row ${row.id} (${row.entry_date}): ${error.message}`);
          }
        }
      }

      if (results.success > 0) {
        const message = results.failed > 0
          ? `Updated ${results.success} record(s). ${results.failed} failed.`
          : `Successfully updated ${results.success} record(s)!`;
        setStatus({ type: results.failed > 0 ? 'error' : 'success', message });
        if (results.errors.length > 0) {
          console.error('Update errors:', results.errors);
        }
      } else {
        throw new Error(`Failed to update any records. ${results.errors.join('; ')}`);
      }
    } catch (error: any) {
      throw new Error(`Failed to save: ${error.message}`);
    }
  };

  return (
    <div className="w-full h-full flex flex-col overflow-hidden relative">
      <div className="w-full h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="text-white px-3 py-2" style={{ background: 'linear-gradient(to right, var(--color-primary-blue), var(--color-secondary-tata-blue))' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database size={16} />
              <div>
                <h1 className="text-sm font-bold" style={{ color: '#ffffff', fontWeight: 700 }}><strong>Inverters </strong>{siteName}</h1>
                {/* <p className="text-xs" style={{ color: '#ffffff', opacity: 0.95 }}>
                   • {(startDate || endDate) ? `${filteredDates.length} of ${dates.length}` : dates.length} dates
                  {isFetching && ' • Refreshing...'}
                  {isSaving && ' • Saving...'}
                </p> */}
              </div>
            </div>
            <FormActions
              isCondensed={isCondensed}
              onToggleView={() => setIsCondensed(!isCondensed)}
              isExporting={isExporting}
              isSaving={isSaving}
              loadingSummary={false}
              hasData={true}
              onAddRow={addDate}
              onShowSummary={fetchSummary}
              onSave={handleSave}
              onExport={exportData}
              addRowLabel="New Date"
              customActions={
                <>
                  <DateRangeFilter
                    startDate={startDate}
                    endDate={endDate}
                    onDateChange={(start: string | null, end: string | null) => {
                      setStartDate(start);
                      setEndDate(end);
                    }}
                  />
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleCorrectMistake}
                    disabled={loadingCorrectMistake}
                    className="flex items-center gap-1 px-3 py-1 rounded text-xs text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'var(--color-secondary-orange)' }}
                    title="Edit previously entered data"
                  >
                    {loadingCorrectMistake ? (
                      <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                    ) : (
                      <Edit size={12} />
                    )}
                    <span className="hidden sm:inline">Edit</span>
                  </motion.button>
                </>
              }
            />
          </div>
        </div>

        {status.type && <StatusBanner status={status.type} message={status.message} onClose={() => setStatus({ type: null, message: '' })} />}

        {/* Table (same as before) */}
        <div className="flex-1 min-h-0">
          <div
            ref={(el) => {
              containerRef.current = el;
              // Don't set scroll position here - let useEffect handle it
              // This prevents conflicts with scroll position preservation during lazy loading
            }}
            className="w-full h-full overflow-auto"
            style={{ position: 'relative', overflowX: 'auto', overflowY: 'auto' }}
          >
            {/* ... all your table code stays the same ... */}
            <style>{`
              table { 
                border-collapse: separate !important;
                border-spacing: 0 !important;
                font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important; 
                table-layout: fixed !important;
                width: auto !important;
                background: #ffffff !important;
              }
              th, td { 
                border: none !important;
                border-right: 1px solid #e5e7eb !important;
                border-bottom: 1px solid #e5e7eb !important;
                padding: 6px 8px !important;
                font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important; 
                box-sizing: border-box !important;
                transition: background-color 0.15s ease, box-shadow 0.15s ease !important;
              }
              th {
                background: #f9fafb !important;
                border-bottom: 2px solid #e5e7eb !important;
              }
              td {
                background: #ffffff !important;
              }
              td:hover {
                background: #f3f4f6 !important;
              }
              /* Modern input styling */
              td input[type="text"],
              td input[type="number"] {
                width: 100% !important;
                height: 100% !important;
                border: none !important;
                outline: none !important;
                background: transparent !important;
                padding: 4px 2px 4px 14px !important;
                margin: 0 !important;
                text-align: center !important;
                font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
                transition: all 0.15s ease !important;
                border-radius: 4px !important;
              }
              td input[type="text"]:focus,
              td input[type="number"]:focus {
                background: #ffffff !important;
                box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3) !important;
                outline: none !important;
              }
              td input[type="text"]:hover:not(:focus),
              td input[type="number"]:hover:not(:focus) {
                background: #f9fafb !important;
              }
              .sticky-icr-col { 
                position: sticky !important; 
                left: 0 !important; 
                z-index: 15 !important; 
                color: #000000 !important; 
                font-weight: 700 !important; 
                font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important; 
                font-size: ${styleVars.fs}px !important; 
                text-align: center !important; 
                background: var(--color-white) !important;
                border-right: 1px solid #e5e7eb !important;
                box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1) !important;
                width: ${styleVars.icrW}px !important;
                min-width: ${styleVars.icrW}px !important;
                max-width: ${styleVars.icrW}px !important;
              }
              .sticky-icr-col::after {
                content: '' !important;
                position: absolute !important;
                top: 0 !important;
                right: -1px !important;
                bottom: 0 !important;
                width: 1px !important;
                background: #e5e7eb !important;
                z-index: 16 !important;
                pointer-events: none !important;
              }
              .sticky-inv-col { 
                position: sticky !important; 
                left: ${styleVars.icrW}px !important; 
                z-index: 15 !important; 
                color: #111827 !important; 
                font-weight: 600 !important; 
                font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important; 
                font-size: ${styleVars.fs}px !important; 
                text-align: center !important; 
                background: #f9fafb !important;
                border-right: 1px solid #e5e7eb !important;
                box-shadow: 2px 0 6px rgba(0, 0, 0, 0.05) !important;
                width: ${styleVars.invW}px !important;
                min-width: ${styleVars.invW}px !important;
                max-width: ${styleVars.invW}px !important;
                padding: 8px 6px !important;
              }
              .sticky-inv-col::after {
                content: '' !important;
                position: absolute !important;
                top: 0 !important;
                right: -1px !important;
                bottom: 0 !important;
                width: 1px !important;
                background: #e5e7eb !important;
                z-index: 16 !important;
                pointer-events: none !important;
              }
              .sticky-header { 
                position: sticky !important; 
                top: 0 !important; 
                z-index: 20 !important; 
                font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
                background: #f3f4f6 !important;
                border-bottom: 2px solid #d1d5db !important;
                padding: 10px 8px !important;
                font-weight: 600 !important;
                color: #374151 !important;
                letter-spacing: 0.3px !important;
              }
              .sticky-header-icr { 
                position: sticky !important; 
                top: 0 !important; 
                left: 0 !important; 
                z-index: 25 !important; 
                color: #111827 !important; 
                font-weight: 600 !important; 
                font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important; 
                font-size: ${styleVars.hfs}px !important; 
                text-align: center !important; 
                background: #f3f4f6 !important;
                border-right: 1px solid #e5e7eb !important;
                box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.08) !important;
                width: ${styleVars.icrW}px !important;
                min-width: ${styleVars.icrW}px !important;
                max-width: ${styleVars.icrW}px !important;
                padding: 10px 8px !important;
                letter-spacing: 0.3px !important;
              }
              .sticky-header-icr::after {
                content: '' !important;
                position: absolute !important;
                top: 0 !important;
                right: -1px !important;
                bottom: 0 !important;
                width: 1px !important;
                background: #e5e7eb !important;
                z-index: 26 !important;
                pointer-events: none !important;
              }
              .sticky-header-inv { 
                position: sticky !important; 
                top: 0 !important; 
                left: ${styleVars.icrW}px !important; 
                z-index: 25 !important; 
                color: #111827 !important; 
                font-weight: 600 !important; 
                font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important; 
                font-size: ${styleVars.hfs}px !important; 
                text-align: center !important; 
                background: #f3f4f6 !important;
                border-right: 1px solid #e5e7eb !important;
                box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.08) !important;
                width: ${styleVars.invW}px !important;
                min-width: ${styleVars.invW}px !important;
                max-width: ${styleVars.invW}px !important;
                padding: 10px 8px !important;
                letter-spacing: 0.3px !important;
              }
              .sticky-header-inv::after {
                content: '' !important;
                position: absolute !important;
                top: 0 !important;
                right: -1px !important;
                bottom: 0 !important;
                width: 1px !important;
                background: #e5e7eb !important;
                z-index: 26 !important;
                pointer-events: none !important;
              }
              /* TOTAL row styling - restore previous background */
              .sticky-inv-col[style*="background: var(--color-primary-blue-light)"] {
                background: var(--color-primary-blue-light) !important;
                border-right: 1px solid #e5e7eb !important;
              }
              /* Make TOTAL row values bold and visible */
              .total-row {
                background: var(--color-primary-blue-light) !important;
              }
              .total-row td {
                background: var(--color-primary-blue-light) !important;
                font-weight: 700 !important;
                color: #000000 !important;
                vertical-align: middle !important;
              }
              .total-row td span,
              .total-value-cell span {
                font-weight: 700 !important;
                font-size: ${styleVars.fs + 2}px !important;
                color: #000000 !important;
              }
              .total-row .sticky-inv-col {
                background: var(--color-primary-blue-light) !important;
                font-weight: 700 !important;
                color: #000000 !important;
              }
              .total-row .sticky-inv-col span {
                font-weight: 700 !important;
                color: #000000 !important;
                font-size: ${styleVars.fs + 2}px !important;
              }
              /* Force all ICR, Inverter, and TOTAL labels to be modern styled */
              .sticky-icr-col,
              .sticky-inv-col,
              .sticky-header-icr,
              .sticky-header-inv,
              .sticky-icr-col *,
              .sticky-inv-col *,
              .sticky-header-icr *,
              .sticky-header-inv *,
              .sticky-icr-col span,
              .sticky-inv-col span,
              .sticky-header-icr span,
              .sticky-header-inv span {
                color: #111827 !important;
                font-weight: 600 !important;
                font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
                text-align: center !important;
              }
              /* Override for TOTAL rows - make them bold */
              tr[style*="var(--color-primary-blue-light)"] .sticky-inv-col,
              tr[style*="var(--color-primary-blue-light)"] .sticky-inv-col span {
                font-weight: 700 !important;
                color: #000000 !important;
              }
              tr[style*="var(--color-primary-blue-light)"] td {
                vertical-align: middle !important;
              }
              tr[style*="var(--color-primary-blue-light)"] td span {
                font-weight: 700 !important;
                color: #000000 !important;
                font-size: ${styleVars.fs + 2}px !important;
              }
              .grand-total-row .sticky-icr-col,
              .grand-total-row .sticky-inv-col,
              .grand-total-row .sticky-icr-col span,
              .grand-total-row .sticky-inv-col span {
                font-weight: 700 !important;
                font-size: ${styleVars.fs + 1}px !important;
                letter-spacing: 0.3px !important;
                color: #000000 !important;
              }
              .grand-total-row td {
                background: var(--color-primary-blue-light) !important;
                font-weight: 700 !important;
                color: #000000 !important;
                vertical-align: middle !important;
              }
              .grand-total-row td span {
                font-weight: 700 !important;
                font-size: ${styleVars.fs + 1}px !important;
                color: #000000 !important;
              }
              /* Override: Inputs inside sticky columns should NOT be bold */
              .sticky-icr-col input,
              .sticky-inv-col input,
              .sticky-icr-col input[type="text"],
              .sticky-inv-col input[type="text"],
              .sticky-icr-col input[type="number"],
              .sticky-inv-col input[type="number"],
              .sticky-icr-col .databank-input,
              .sticky-inv-col .databank-input {
                font-weight: 400 !important;
              }
              .date-header-container { 
                display: flex; 
                align-items: center; 
                justify-content: center; 
                gap: 4px; 
                padding: 6px 8px; 
                font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
                border-radius: 4px;
                transition: background-color 0.15s ease;
              }
              .date-header-container:hover {
                background: #f3f4f6 !important;
              }
              .date-display { 
                flex: 1; 
                text-align: center; 
                color: #1C4D88; 
                font-weight: 500 !important; 
                font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important; 
                font-size: ${styleVars.hfs}px !important; 
                user-select: none;
                letter-spacing: 0.2px;
              }
              input { font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important; font-size: ${styleVars.fs}px !important; font-weight: 400 !important; }
              input[type="text"], input[type="number"] { font-weight: 400 !important; }
              td input, td input[type="text"], td input[type="number"] { font-weight: 400 !important; }
              table td input, table td input[type="text"], table td input[type="number"] { font-weight: 400 !important; }
              tbody td input, tbody td input[type="text"], tbody td input[type="number"] { font-weight: 400 !important; }
              table tbody td input, table tbody td input[type="text"], table tbody td input[type="number"] { font-weight: 400 !important; }
              /* Remove ALL borders from date inputs */
              input[type="date"],
              input[type="date"].date-input-hidden,
              .date-input-hidden,
              input[type="date"][id] {
                border: none !important;
                border-width: 0 !important;
                border-style: none !important;
                border-color: transparent !important;
                border-top: none !important;
                border-right: none !important;
                border-bottom: none !important;
                border-left: none !important;
                outline: none !important;
                outline-width: 0 !important;
                box-shadow: none !important;
                -webkit-box-shadow: none !important;
                -moz-box-shadow: none !important;
                position: absolute !important;
                opacity: 0 !important;
                pointer-events: none !important;
              }
              /* Hide native calendar icon from date inputs */
              input[type="date"]::-webkit-calendar-picker-indicator,
              input[type="date"].date-input-hidden::-webkit-calendar-picker-indicator,
              .date-input-hidden::-webkit-calendar-picker-indicator,
              input[type="date"][id]::-webkit-calendar-picker-indicator {
                display: none !important;
                opacity: 0 !important;
                width: 0 !important;
                height: 0 !important;
                pointer-events: none !important;
                cursor: default !important;
              }
              input[type="date"]::-moz-calendar-picker-indicator,
              input[type="date"].date-input-hidden::-moz-calendar-picker-indicator,
              .date-input-hidden::-moz-calendar-picker-indicator,
              input[type="date"][id]::-moz-calendar-picker-indicator {
                display: none !important;
                opacity: 0 !important;
                width: 0 !important;
                height: 0 !important;
                pointer-events: none !important;
                cursor: default !important;
              }
              input[type="date"]:focus,
              input[type="date"].date-input-hidden:focus,
              .date-input-hidden:focus,
              input[type="date"][id]:focus {
                border: none !important;
                border-width: 0 !important;
                border-style: none !important;
                border-color: transparent !important;
                border-top: none !important;
                border-right: none !important;
                border-bottom: none !important;
                border-left: none !important;
                outline: none !important;
                outline-width: 0 !important;
                box-shadow: none !important;
                -webkit-box-shadow: none !important;
                -moz-box-shadow: none !important;
              }
              .delete-date-btn { 
                background: transparent; 
                border: none; 
                color: var(--color-error, #dc2626); 
                cursor: pointer; 
                padding: 0; 
                display: flex; 
                align-items: center; 
                justify-content: center;
                opacity: 0.7;
                transition: opacity 0.2s;
              }
              .delete-date-btn:hover { opacity: 1; }
            `}</style>
            <table style={{ borderCollapse: 'collapse', fontSize: `${styleVars.fs}px`, tableLayout: 'fixed', width: `${styleVars.icrW + styleVars.invW + columnsTotalSize}px`, minWidth: `${styleVars.icrW + styleVars.invW + columnsTotalSize}px` }}>
              <thead>
                <tr style={{ height: `${styleVars.hh}px` }}>
                  <th className="sticky-header-icr" style={{ width: `${styleVars.icrW}px`, background: 'var(--color-gray-200)', fontSize: `${styleVars.hfs}px`, fontWeight: 700, color: '#000000', textAlign: 'center', verticalAlign: 'middle' }}>
                    <span style={{ color: '#000000', fontWeight: 700, display: 'block', width: '100%', textAlign: 'center' }}>ICR</span>
                  </th>
                  <th className="sticky-header-inv" style={{ width: `${styleVars.invW}px`, background: 'var(--color-gray-200)', fontSize: `${styleVars.hfs}px`, fontWeight: 700, color: '#000000', textAlign: 'center', verticalAlign: 'middle' }}>
                    <span style={{ color: '#000000', fontWeight: 700, display: 'block', width: '100%', textAlign: 'center' }}>Inverter</span>
                  </th>
                  {columnPaddingLeft > 0 && (
                    <th aria-hidden="true" style={{ width: `${columnPaddingLeft}px`, padding: 0, border: 'none' }} />
                  )}
                  {virtualColumns.map((vCol) => {
                    const i = vCol.index;
                    const d = filteredDates[i];
                    if (d === undefined) return null;
                    return (
                    <th key={i} className="sticky-header" style={{ width: `${styleVars.w}px`, background: 'var(--color-gray-200)', fontSize: `${styleVars.hfs}px`, position: 'relative', fontWeight: 700, color: '#000000' }}>
                      <div className="date-header-container">
                        <span
                          className="date-display"
                          onClick={() => {
                            const input = document.getElementById(`date-input-${i}`) as HTMLInputElement;
                            input?.showPicker?.();
                          }}
                          style={{ fontSize: `${styleVars.hfs}px`, cursor: 'pointer' }}
                        >
                          {formatDateToDisplay(d)}
                        </span>
                        {dates.length > 1 && (
                          <button
                            className="delete-date-btn"
                            onClick={() => deleteDate(i)}
                            title="Delete this date"
                            style={{ width: '14px', height: '14px' }}
                          >
                            <X size={11} />
                          </button>
                        )}
                      </div>
                      <input
                        id={`date-input-${i}`}
                        type="date"
                        value={d}
                        onChange={(e) => updateDate(i, e.target.value)}
                        className="date-input-hidden"
                        style={{
                          border: 'none',
                          borderWidth: 0,
                          borderStyle: 'none',
                          borderColor: 'transparent',
                          outline: 'none',
                          boxShadow: 'none'
                        }}
                        ref={(el) => {
                          if (el) {
                            // Force remove borders using setProperty
                            el.style.setProperty('border', 'none', 'important');
                            el.style.setProperty('border-width', '0', 'important');
                            el.style.setProperty('border-style', 'none', 'important');
                            el.style.setProperty('border-color', 'transparent', 'important');
                            el.style.setProperty('outline', 'none', 'important');
                            el.style.setProperty('box-shadow', 'none', 'important');
                          }
                        }}
                      />
                    </th>
                    );
                  })}
                  {columnPaddingRight > 0 && (
                    <th aria-hidden="true" style={{ width: `${columnPaddingRight}px`, padding: 0, border: 'none' }} />
                  )}
                </tr>
              </thead>
              <tbody>
                {ALL_ICRS.map((icr, icrIdx) => (
                  <DatabankIcrGroup
                    key={icr}
                    icr={icr}
                    icrIdx={icrIdx}
                    groupData={data[icr]}
                    filteredDates={filteredDates}
                    virtualColumns={virtualColumns}
                    columnPaddingLeft={columnPaddingLeft}
                    columnPaddingRight={columnPaddingRight}
                    styleVars={styleVars}
                    focusedCell={focusedCell}
                    inputRefs={inputRefs}
                    onCellChange={updateCell}
                    onCellKeyDown={handleKeyDown}
                    onCellFocus={handleCellFocus}
                  />
                ))}
                <tr className="grand-total-row" style={{ height: `${styleVars.h}px`, background: 'var(--color-primary-blue-light)' }}>
                  <td
                    className="sticky-icr-col"
                    style={{
                      width: `${styleVars.icrW}px`,
                      background: 'var(--color-white)',
                      color: '#000000',
                      fontWeight: 700,
                      textAlign: 'center',
                      verticalAlign: 'middle'
                    }}
                  >
                    <span style={{ color: '#111827', fontWeight: 700, display: 'block', width: '100%', textAlign: 'center', fontSize: `${styleVars.fs + 2}px` }}>
                      GRAND TOTAL
                    </span>
                  </td>
                  <td
                    className="sticky-inv-col"
                    style={{
                      width: `${styleVars.invW}px`,
                      fontWeight: 700,
                      color: '#000000',
                      textAlign: 'center',
                      background: 'var(--color-primary-blue-light) !important'
                    }}
                  >
                    <span style={{ color: '#000000', fontWeight: 700, display: 'block', width: '100%', fontSize: `${styleVars.fs + 2}px` }}>TOTAL</span>
                  </td>
                  {columnPaddingLeft > 0 && (
                    <td aria-hidden="true" style={{ width: `${columnPaddingLeft}px`, padding: 0, border: 'none' }} />
                  )}
                  {virtualColumns.map((vCol) => {
                    const date = filteredDates[vCol.index];
                    if (date === undefined) return null;
                    return (
                    <td key={`grand-${date}`} style={{ width: `${styleVars.w}px`, fontWeight: 700, color: '#000000', textAlign: 'center' }}>
                      <span style={{ color: '#000000', fontWeight: 700, display: 'block', width: '100%', fontSize: `${styleVars.fs + 2}px` }}>
                        {calculateGrandTotal(date).toFixed(1)}
                      </span>
                    </td>
                    );
                  })}
                  {columnPaddingRight > 0 && (
                    <td aria-hidden="true" style={{ width: `${columnPaddingRight}px`, padding: 0, border: 'none' }} />
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary */}
        {showSummary && (
          <SummaryModal
            isOpen={showSummary}
            onClose={() => setShowSummary(false)}
            title={`Inverters Summary - ${siteName}`}
            data={backendData}
            columns={[
              { key: 'entry_date', label: 'Date', format: (v) => formatDateToDisplay(v) },
              { key: 'icr', label: 'ICR' },
              { key: 'inverter', label: 'Inverter' },
              { key: 'energy_kwh', label: 'Energy (kWh)', format: (v) => v ? Number(v).toFixed(1) : '-' }
            ]}
          />
        )}

        {/* Correct Mistake Modal */}
        {showCorrectMistake && (
          <CorrectMistakeModal
            isOpen={showCorrectMistake}
            onClose={() => setShowCorrectMistake(false)}
            title="Edit - Inverters"
            data={correctMistakeData}
            columns={DATABANK_COLUMNS}
            onSave={handleSaveCorrectedData}
            isLoading={loadingCorrectMistake}
            siteCode="axon"
            onLoadMore={loadMoreCorrectMistakeData}
            isLoadingMore={correctMistakeLoadingMore}
            hasMore={correctMistakeHasMore}
          />
        )}

        {/* Footer */}
        <div className="px-4 py-2 text-xs border-t" style={{ background: 'var(--color-gray-100)', color: '#000000', fontWeight: 400 }}>
          <span>{footerText}</span>
        </div>
      </div>
    </div>
  );
};


